import { useMemo } from 'react';
import { useQuery, type QueryClient } from '@tanstack/react-query';
import type { PlanChangedEvent, PlanItemDTO, PlanSnapshot } from '@clear/contracts';
import { api } from '../lib/api';
import { syncServerTime } from '../lib/time';
import { useOutbox } from '../offline/hooks';
import { applyPending } from '../offline/optimistic';
import { useLiveStatus } from '../live/stream';

/** Stable empty list so memoised selectors don't recompute while loading. */
export const NO_ITEMS: PlanItemDTO[] = [];

export interface Slot {
  lineId?: string | undefined;
  date?: string | undefined;
  shiftId?: string | undefined;
}

export const planKey = (s: Slot) =>
  ['plan', s.lineId ?? 'default', s.date && s.shiftId ? `${s.date}:${s.shiftId}` : 'current'] as const;

/**
 * The plan for a line/date/shift (default: the running shift), kept live by SSE and
 * overlaid with this device's unsynced taps. Falls back to polling if the live stream
 * is down.
 */
export function usePlan(slot: Slot = {}) {
  const live = useLiveStatus();
  const q = useQuery({
    queryKey: planKey(slot),
    queryFn: async () => {
      const p = new URLSearchParams();
      if (slot.lineId) p.set('lineId', slot.lineId);
      if (slot.date && slot.shiftId) {
        p.set('date', slot.date);
        p.set('shiftId', slot.shiftId);
      }
      const sent = Date.now();
      const s = await api<PlanSnapshot>(`/plan${p.size ? `?${p}` : ''}`);
      syncServerTime(s.serverTime, sent);
      return s;
    },
    staleTime: 30_000,
    refetchInterval: live === 'open' ? 5 * 60_000 : 15_000,
    networkMode: 'offlineFirst',
  });
  const queue = useOutbox();
  const data = useMemo(() => (q.data ? applyPending(q.data, queue) : undefined), [q.data, queue]);
  return { ...q, data };
}

/** Merge a live event into every cached snapshot of that plan, or refetch on a gap. */
export function applyPlanEvent(qc: QueryClient, e: PlanChangedEvent): void {
  let needsRefetch = e.refetch;
  const caches = qc.getQueriesData<PlanSnapshot>({ queryKey: ['plan'] });
  for (const [key, snap] of caches) {
    if (!snap) continue;
    if (snap.plan?.id === e.planId) {
      if (!e.refetch && e.version === snap.plan.version + 1) {
        const byId = new Map(e.items.map((i) => [i.id, i]));
        const items = snap.items.map((i) => byId.get(i.id) ?? i);
        for (const i of e.items) if (!snap.items.some((x) => x.id === i.id)) items.push(i);
        qc.setQueryData<PlanSnapshot>(key, { ...snap, plan: e.plan, items });
      } else if (e.version > snap.plan.version) {
        void qc.invalidateQueries({ queryKey: key, exact: true });
      }
    } else if (!snap.plan && snap.lineId === e.lineId) {
      needsRefetch = true;
    }
  }
  if (needsRefetch) void qc.invalidateQueries({ queryKey: ['plan'] });
}
